import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { GenericTable } from "../../../Shared/components/Table/GenericTable";
import { TableActionButtons } from "../../../Shared/components/Table/TableActionButtons";
import { PageOptionsDto } from "../../../Shared/dto/page.options.dto";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { IUser } from "../models/User";
import { UserService } from "../services/user.service";
import { UserForm } from "./UserForm";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { AuthService } from "../../Auth/services/AuthService";
import { FileImportForm } from "../../../Shared/components/FileImportForm";
import {
  ImportFileActionType,
  ImportFileLabelType,
} from "../../../Shared/enums/importFileActionType";
import { ConfirmDialog } from "../../../Shared/components/ConfirmDialog";
import { useTranslation } from "react-i18next";

export const Users = () => {
  const navigate = useNavigate();
  const {t} = useTranslation()
  const message = t('loading_message');
  const isAllowed = AuthService.isManagementAllowed();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();
  const [selectedUser, setSelectedUser] = useState<IUser>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const findUsers = async () => {
    try {
      const pageOptions: PageOptionsDto = {
        page: pagination.pageIndex + 1,
        take: pagination.pageSize,
        query: globalFilter,
      };
      setIsLoading(true);
      return await UserService.getAll(pageOptions);
    } catch (error: any) {
      setIsLoading(false);
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const query = useQuery({
    queryKey: [
      "users",
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
    ],
    queryFn: findUsers,
    placeholderData: (previous) => previous?.data,
  });

  const debouncedSetFilter = useMemo(
    () =>
      debounce((val: string) => {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        setGlobalFilter(val);
      }, 200),
    []
  );

  const onPaginationChange = (pageIndex: number, pageSize: number) => {
    setPagination({ pageIndex, pageSize });
  };

  const getPageCount = () => {
    const total = query.data?.meta?.itemCount ?? 0;
    return Math.ceil(total / pagination.pageSize);
  };

  const onRowEditAction = (row: IUser) => {
    setSelectedUser(row);
    onAddAction();
  };

  const onRowViewAction = (row: IUser) => {
    setSelectedUser(row);
    navigate(`/services/settings/account/?user=${row.id}`);
  };

  const onRowDeleteAction = (row: IUser) => {
    setSelectedUser(row);
    setIsDialogOpen(true);
  };

  const onExportAction = () => {
    UtilService.exportToExcel(query?.data?.data, t('user'));
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const onAddAction = () => setIsModalOpen(!isModalOpen);
  const toggleModal = (success: boolean) => {
    setIsModalOpen(!isModalOpen);
    if (success) {
      query.refetch();
    }
  };

  const deleteAction = async () => {
    const approvals =
      selectedUsers?.length > 0
        ? selectedUsers.map((x) => x.id!)
        : [selectedUser?.id ?? ""];
    try {
      const response = await UserService.delete(approvals);
      if (response) {
        query.refetch();
      }
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    }
  };

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const onImportAction = () => setIsImportModalOpen(!isModalOpen);
  const toggleImportModal = (success: boolean) => {
    setIsImportModalOpen(!isImportModalOpen);
    if (success) {
      query.refetch();
    }
  };

  const columns = useMemo<ColumnDef<IUser>[]>(
    () => [
      {
        accessorKey: "firstName",
        header: t('first_name_label'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "lastName",
        header: t('last_name_label'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "phoneNumber",
        header: t('phone_number_label'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "email",
        header: t('email_label'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "createdAt",
        header: t('date_created'),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    []
  );

  const tableAction = isAllowed ? (
    <TableActionButtons
      onAdd={onAddAction}
      onImport={onImportAction}
      onExport={onExportAction}
    />
  ) : (
    <TableActionButtons onExport={onExportAction} />
  );

  return (
    <div className="">
      <div className="relative">
        {isModalOpen && <UserForm onClose={toggleModal} user={selectedUser} />}

        {isImportModalOpen && (
          <FileImportForm
            onClose={toggleImportModal}
            actionType={ImportFileActionType.USERS}
            actionLabel={ImportFileLabelType.USERS}
          />
        )}
        <ConfirmDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onConfirm={deleteAction}
          itemName={`${selectedUser.firstName}  ${selectedUser?.lastName}`}
        />
        <GenericTable
          isLoading={isLoading}
          data={query?.data?.data ?? []}
          meta={query?.data?.meta ?? 0}
          columns={columns}
          globalFilter={globalFilter}
          onGlobalFilterChange={debouncedSetFilter}
          pageCount={getPageCount()}
          onPaginationChange={onPaginationChange}
          onRowSelectionChange={setSelectedUsers}
          onEdit={onRowEditAction}
          onMore={onRowViewAction}
          onDelete={onRowDeleteAction}
          actionButtons={tableAction}
        />
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </div>
  );
};
